import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../api/apiClient";
import { useT } from "../../i18n/LanguageProvider";
import { useAuth } from "../../state/AuthContext";
import { NewPasswordForm } from "./NewPasswordForm";
import {
  AccountShell,
  errorStatus,
  useAccountLanguage,
  useLinkPreview,
  useLinkToken,
} from "./accountLink";

/**
 * G-136 — `/account/setup#token=…`, from the invitation email. The invited
 * person chooses their own password and is signed straight in through the
 * ordinary login, so the same lockout and audit trail apply as any sign-in.
 */
export function AccountSetupPage() {
  const token = useLinkToken();
  const link = useLinkPreview(token, ["INVITE"]);
  useAccountLanguage(link.kind === "ready" ? link.preview.language : null);
  const { t } = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alreadySetUp, setAlreadySetUp] = useState(false);
  // Set once the password is saved: from then on the link is used up, so a
  // failing sign-in must never send the person back to it.
  const [setupDone, setSetupDone] = useState(false);

  if (link.kind === "loading") {
    return (
      <AccountShell>
        <p className="page-subtitle account-center">{t("account.loading")}</p>
      </AccountShell>
    );
  }

  if (setupDone) {
    return (
      <AccountShell>
        <h1 className="page-title account-center">{t("account.setupDoneTitle")}</h1>
        <p className="account-center">{t("account.setupDoneBody")}</p>
        <Link className="button" to="/login">
          {t("account.goToSignIn")}
        </Link>
      </AccountShell>
    );
  }

  if (link.kind === "unusable" || alreadySetUp) {
    return (
      <AccountShell>
        <h1 className="page-title account-center">{t("account.linkUnusableTitle")}</h1>
        <p className="account-center">
          {alreadySetUp ? t("account.alreadySetUp") : t("account.linkUnusable")}
        </p>
        <Link className="button" to="/login">
          {t("account.goToSignIn")}
        </Link>
      </AccountShell>
    );
  }

  const { preview } = link;
  const submit = async (password: string) => {
    setError(null);
    setBusy(true);
    let email: string;
    try {
      ({ email } = await api.completeAccountSetup(token, password));
    } catch (err: unknown) {
      const status = errorStatus(err);
      if (status === 409) setAlreadySetUp(true);
      else setError(status === 410 ? t("account.linkUnusable") : t("account.genericError"));
      setBusy(false);
      return;
    }
    try {
      await login(email, password);
      navigate("/app", { replace: true });
    } catch {
      // The password IS saved; only the automatic sign-in failed (a busy
      // shop connection can hit the sign-in limit). Say so, and send them to
      // the sign-in page — never back to a link that no longer works.
      setSetupDone(true);
    }
  };

  return (
    <AccountShell>
      <h1 className="page-title account-center">{t("account.setupTitle")}</h1>
      <p className="account-center">
        {preview.firstName
          ? t("account.setupLead", { name: preview.firstName, email: preview.email })
          : t("account.setupLeadNoName", { email: preview.email })}
      </p>
      <NewPasswordForm
        submitLabel={t("account.setupSubmit")}
        error={error}
        busy={busy}
        onSubmit={(password) => void submit(password)}
      />
    </AccountShell>
  );
}
